// Story 10.3 — Panel con datos de sesión. Resalta keys especiales.

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { ConversationSessionDto } from '@/lib/conversations-types';
import { formatRelative } from '@/lib/relative-time';

interface Props {
  session: ConversationSessionDto;
  messageCount: number;
}

const HIGHLIGHT_KEYS = new Set(['userName', 'lastOrderItems', 'flowId', 'stepIndex']);

function renderValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function SessionPanel({ session, messageCount }: Props): React.ReactElement {
  const customEntries = Object.entries(session.sessionData ?? {});
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Datos de sesión</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
          <dt className="text-muted-foreground">Usuario</dt>
          <dd>{session.userName ?? '—'}</dd>
          <dt className="text-muted-foreground">Teléfono</dt>
          <dd className="font-mono text-xs">{session.userPhone}</dd>
          <dt className="text-muted-foreground">Flow actual</dt>
          <dd>
            {session.flowId ?? '—'}
            {session.stepIndex != null ? ` · paso ${session.stepIndex}` : ''}
          </dd>
          <dt className="text-muted-foreground">Estado</dt>
          <dd className="uppercase">{session.status}</dd>
          <dt className="text-muted-foreground">Mensajes</dt>
          <dd>{messageCount}</dd>
          <dt className="text-muted-foreground">Creada</dt>
          <dd>{formatRelative(session.createdAt)}</dd>
          <dt className="text-muted-foreground">Expira</dt>
          <dd>{formatRelative(session.expiresAt)}</dd>
        </dl>
        {customEntries.length > 0 && (
          <div>
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Datos custom
            </h4>
            <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
              {customEntries.map(([k, v]) => (
                <div key={k} className="contents">
                  <dt
                    className={
                      HIGHLIGHT_KEYS.has(k)
                        ? 'font-medium text-foreground'
                        : 'text-muted-foreground'
                    }
                  >
                    {k}
                  </dt>
                  <dd className="break-words font-mono text-xs">
                    <pre className="whitespace-pre-wrap">{renderValue(v)}</pre>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
